import { useTranslation } from "react-i18next";
import { TrendingDown, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge.tsx";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card.tsx";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart.tsx";
import { Line, LineChart, XAxis, YAxis, CartesianGrid } from "recharts";

export type DashboardStatsChartData = {
  completedCount: number;
  totalCount: number;
  percentChangeToday: number;
  timeSeries: { day: string; total: number }[];
};

type DashboardStatsChartProps = {
  data: DashboardStatsChartData;
  statDescriptionKey: string;
  totalLabelKey: string;
  chartLabelKey: string;
};

export function DashboardStatsChart({
  data,
  statDescriptionKey,
  totalLabelKey,
  chartLabelKey,
}: DashboardStatsChartProps) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <Card className="@container/card sm:col-span-1">
        <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
          <div className="space-y-2">
            <CardDescription>{t(statDescriptionKey)}</CardDescription>
            <CardTitle className="text-5xl font-semibold tabular-nums">
              {data.completedCount.toLocaleString()}
            </CardTitle>
          </div>
          <Badge
            variant="outline"
            className={`shrink-0 gap-1 ${
              data.percentChangeToday >= 0
                ? "border-status-success/50 text-status-success"
                : "border-status-danger/50 text-status-danger"
            }`}
          >
            {data.percentChangeToday >= 0 ? (
              <TrendingUp className="size-3.5" />
            ) : (
              <TrendingDown className="size-3.5" />
            )}
            {data.percentChangeToday >= 0
              ? `+${Math.abs(data.percentChangeToday)}%`
              : `-${Math.abs(data.percentChangeToday)}%`}
          </Badge>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-2 text-sm">
          <p className="text-muted-foreground">
            {t(totalLabelKey, { total: data.totalCount })}
          </p>
        </CardFooter>
      </Card>
      <Card className="sm:col-span-2">
        <CardContent className="p-6">
          <p className="text-sm font-medium text-text-2">{t(chartLabelKey)}</p>
          <ChartContainer
            className="mt-4 h-[160px] w-full"
            config={{
              total: { label: t(chartLabelKey), color: "rgb(var(--accent) / 1)" },
            }}
          >
            <LineChart
              data={data.timeSeries}
              margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                className="stroke-border"
                vertical={false}
              />
              <XAxis
                dataKey="day"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: "rgb(var(--text-3) / 1)" }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11, fill: "rgb(var(--text-3) / 1)" }}
                width={28}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line
                type="monotone"
                dataKey="total"
                stroke="var(--color-total)"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
}
